'use client';
import React, { useState, useEffect } from 'react';
import { Plus, X, Tag, Edit2, Check } from 'lucide-react';
import {
  fetchClaimCategories,
  insertClaimCategory,
  updateClaimCategory,
  deleteClaimCategory,
  type ClaimCategory,
} from '@/lib/claimService';
import { useClaimContext } from '@/contexts/ClaimContext';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';

export default function CategoriesSection() {
  const { activeClaim } = useClaimContext();
  const claimId = activeClaim?.id;

  const [categories, setCategories] = useState<ClaimCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      if (claimId) {
        const data = await fetchClaimCategories(claimId);
        setCategories(data);
      }
      setLoading(false);
    }
    load();
  }, [claimId]);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed || !claimId) return;
    if (categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('A category with that name already exists');
      return;
    }
    const saved = await insertClaimCategory(claimId, trimmed);
    if (saved) {
      setCategories((prev) => [...prev, saved]);
      setNewName('');
      setIsAdding(false);
      toast.success(`"${trimmed}" added as a category`);
    } else {
      toast.error('Failed to add category');
    }
  };

  const startEdit = (cat: ClaimCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const saveEdit = async () => {
    const trimmed = editName.trim();
    if (!trimmed || !editingId) return;
    if (categories.some((c) => c.id !== editingId && c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('A category with that name already exists');
      return;
    }
    const ok = await updateClaimCategory(editingId, trimmed);
    if (ok) {
      setCategories((prev) =>
        prev.map((c) => (c.id === editingId ? { ...c, name: trimmed } : c))
      );
      toast.success(`Category renamed to "${trimmed}"`);
    } else {
      toast.error('Failed to rename category');
    }
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleRemove = async (id: string, name: string) => {
    const ok = await deleteClaimCategory(id);
    if (ok) {
      setCategories((prev) => prev.filter((c) => c.id !== id));
      toast.success(`"${name}" removed`);
    } else {
      toast.error('Failed to remove category');
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        Loading categories…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag size={16} className="text-primary" />
          <h2 className="text-base font-semibold text-foreground">Categories</h2>
          {activeClaim && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
              {activeClaim.name}
            </span>
          )}
        </div>
        <button
          onClick={() => { setIsAdding(true); setEditingId(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 transition-all duration-150 active:scale-95"
        >
          <Plus size={13} /> Add Category
        </button>
      </div>
      <p className="text-sm text-muted-foreground">
        Manage item categories for <strong>{activeClaim?.name ?? 'this claim'}</strong>. Categories help organize items when entering and exporting inventory.
      </p>

      <div className="space-y-2">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="flex items-center gap-3 p-3 bg-secondary/30 border border-border rounded-lg group"
          >
            <Tag size={14} className="text-muted-foreground shrink-0" />

            {editingId === cat.id ? (
              <div className="flex-1 flex items-center gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 px-2.5 py-1.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                />
                <div className="flex gap-1">
                  <button
                    onClick={saveEdit}
                    disabled={!editName.trim()}
                    className="p-1.5 rounded hover:bg-positive-bg text-positive disabled:opacity-40 transition-colors"
                    title="Save"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
                    title="Cancel"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium text-foreground">{cat.name}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(cat)}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150 active:scale-95"
                    title={`Rename ${cat.name}`}
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget({ id: cat.id, name: cat.name })}
                    className="p-1.5 rounded hover:bg-negative-bg text-muted-foreground hover:text-negative transition-all duration-150 active:scale-95"
                    title={`Remove ${cat.name}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {categories.length === 0 && !isAdding && (
          <div className="py-6 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
            No categories yet. Click <strong>Add Category</strong> to get started.
          </div>
        )}

        {isAdding && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg fade-in">
            <Tag size={14} className="text-primary shrink-0" />
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Category name…"
              className="flex-1 px-2.5 py-1.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') { setIsAdding(false); setNewName(''); }
              }}
            />
            <div className="flex gap-1">
              <button
                onClick={handleAdd}
                disabled={!newName.trim()}
                className="p-1.5 rounded hover:bg-positive-bg text-positive disabled:opacity-40 transition-colors"
                title="Add category"
              >
                <Check size={14} />
              </button>
              <button
                onClick={() => { setIsAdding(false); setNewName(''); }}
                className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Remove category?"
        description={`"${deleteTarget?.name}" will be removed from the list. Items assigned to this category will retain their label but the category won't appear in the dropdown.`}
        confirmLabel="Remove Category"
        onConfirm={() => deleteTarget && handleRemove(deleteTarget.id, deleteTarget.name)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
